import { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import { useHostStatus } from '../api/queries';
import { useSession } from '../state/session';
import { Button, EmptyState, PageLoader, Seo } from '../ui';
import styles from './guards.module.css';

function SignInPrompt({ reason = 'Sign in to continue.' }) {
  const { openAuth } = useSession();
  const opened = useRef(false);

  // Open the dialog straight away; the page stays put so deep links still work after sign-in.
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    openAuth({ view: 'login', reason });
  }, [openAuth, reason]);

  return (
    <div className={`container ${styles.wrap}`}>
      <Seo title="Sign in | Nappr" noindex />
      <EmptyState
        icon={<LockKeyhole />}
        title="You’re signed out"
        description={reason}
        action={
          <>
            <Button onClick={() => openAuth({ view: 'login', reason })}>Sign in</Button>
            <Button variant="secondary" onClick={() => openAuth({ view: 'register', reason })}>
              Create an account
            </Button>
          </>
        }
      />
    </div>
  );
}

export function RequireAuth({ children, reason }) {
  const { isSignedIn } = useSession();
  if (!isSignedIn) return <SignInPrompt reason={reason} />;
  return children;
}

/** Host portal pages: active hosts, or members invited to a property. */
export function RequireHost({ children }) {
  const { isSignedIn, user } = useSession();
  const hostStatus = useHostStatus();

  if (!isSignedIn) return <SignInPrompt reason="Sign in to manage your properties." />;
  if (hostStatus.isLoading) return <PageLoader label="Checking your host access…" />;

  const status = hostStatus.data;
  const isActiveHost = Boolean(status?.isHost) && status?.status !== 'suspended';
  if (!isActiveHost && !user?.has_property_access) return <Navigate to="/self-host" replace />;

  return children;
}
